import React, { useState, useMemo } from 'react'
import { Col, Form, Modal, Row, Button } from 'react-bootstrap'
import TagsInput from './TagsInput'

// Image upload function using the provided backend upload endpoint
const uploadToImageKitApi = async (file) => {
  try {
      const uploadUrl = process.env.REACT_APP_ENV === 'local' ? process.env.REACT_APP_BACKEND_API_LOCAL : process.env.REACT_APP_BACKEND_API;
    // const uploadUrl = process.env.REACT_APP_IMAGEKIT_UPLOAD_URL || 'http://localhost:3004/api/imagekit/upload';
    const authToken = process.env.REACT_APP_IMAGEKIT_UPLOAD_TOKEN || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6IjZhMjRkOTU5ZjNkZWE4YmFjYWE3NTQ0YSIsImVtYWlsIjoic2hpdmt1bWFydmFybWE3QGdtYWlsLmNvbSIsImlhdCI6MTc4MTE0NTE1MCwiZXhwIjoxNzgxNzQ5OTUwfQ.yGeoWla_5PLjWvGT08ZbLWwJLKodaZagw7SCrz-QED0';

    const formData = new FormData();
    formData.append('image', file);

    // const response = await fetch(`${uploadUrl}/api/imagekit/upload`, {
    const response = await fetch(`${uploadUrl}/api/r2/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${authToken}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`Upload failed: ${errorData.message || response.statusText}`);
    }

    const result = await response.json();
    const data = result.data || result;
    return {
      url: data.url || data.imageUrl || data.fileUrl,
      fileId: data.fileId || data.id,
      name: data.name || data.fileName || file.name,
    };
  } catch (error) {
    console.error('Backend upload error:', error);
    throw error;
  }
};

function ContentModal({
  show,
  title,
  onClose,
  onSubmit,
}) {
  const [mediaItems, setMediaItems] = useState([
    {
      id: 0,
      name: '',
      fileType: 'image',
      file: null,
      imageUrl: null,
      previewUrl: null,
      fileName: '',
      tags: [],
      isRemoving: false,
      isUploading: false,
    },
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleAddItem = () => {
    const newId = Math.max(...mediaItems.map(item => item.id), -1) + 1;
    setMediaItems([
      ...mediaItems,
      {
        id: newId,
        name: '',
        fileType: 'image',
        file: null,
        imageUrl: null,
        fileName: '',
        tags: [],
        isRemoving: false,
        isUploading: false,
      },
    ]);
  };

  const handleRemoveItem = (id) => {
    // Set removing state with animation
    setMediaItems(mediaItems.map(item =>
      item.id === id ? { ...item, isRemoving: true } : item
    ));

    // Actually remove after animation
    setTimeout(() => {
      setMediaItems(mediaItems.filter(item => item.id !== id));
    }, 300);
  };

  const updateItem = (id, updates) => {
    setMediaItems(mediaItems.map(item =>
      item.id === id ? { ...item, ...updates } : item
    ));
  };

  const handleNameChange = (id, e) => {
    updateItem(id, { name: e.target.value });
  };

  const handleFileTypeChange = (id, e) => {
    updateItem(id, { fileType: e.target.value });
  };

  const handleFileChange = async (id, e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const previewUrl = URL.createObjectURL(selectedFile);
    updateItem(id, {
      file: selectedFile,
      previewUrl,
      isUploading: true,
      error: null,
    });

    try {
      const uploadResult = await uploadToImageKitApi(selectedFile);

      updateItem(id, {
        file: null,
        imageUrl: uploadResult.url,
        previewUrl: uploadResult.url,
        fileName: uploadResult.name,
        name: uploadResult.name,
        isUploading: false,
      });
    } catch (err) {
      console.error('Error uploading image:', err);
      setError('Failed to upload image. Please try again.');
      updateItem(id, { file: null, isUploading: false, previewUrl: null });
    }
  };

  const handleRemoveImage = (id) => {
    updateItem(id, {
      file: null,
      imageUrl: null,
      previewUrl: null,
      fileName: '',
      isUploading: false,
    });
  };

  const handleTagsChange = (id, tags) => {
    updateItem(id, { tags });
  };

  const getFileAccept = (fileType) => {
    return fileType === 'video' ? 'video/*' : 'image/*';
  };

  const getPreviewUrl = (file) => {
    if (!file) return null;
    return URL.createObjectURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const apiBaseUrl = process.env.REACT_APP_ENV === 'local' ? process.env.REACT_APP_BACKEND_API_LOCAL : process.env.REACT_APP_BACKEND_API;
      const apiEndpoint = `${apiBaseUrl}/api/home/submit-media`;
      const authToken = process.env.REACT_APP_AUTH_TOKEN || null;

      // Prepare array of media items
      const validItems = mediaItems.filter(item => item.imageUrl && item.fileType);
      // const validItems = mediaItems.filter(item => item.imageUrl && item.fileType && item.tags?.length > 0);

      if (validItems.length === 0) {
        setError('Please upload at least one image and add tags before submitting.');
        setIsLoading(false);
        return;
      }

      // Format items according to API requirements
      const submitData = validItems.map(item => ({
        name: item.name || item.fileName,
        fileName: item.fileName,
        url: item.imageUrl,
        type: item.fileType,
        tags: item.tags,
      }));

      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify(submitData),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();

      // Call the onSubmit callback if provided
      if (onSubmit) {
        onSubmit(result);
      }

      // Reset form and close modal
      handleClose();
    } catch (err) {
      console.error('Error submitting media:', err);
      setError(err.message || 'Failed to upload media. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setMediaItems([
      {
        id: 0,
        name: '',
        fileType: 'image',
        file: null,
        imageUrl: null,
        fileName: '',
        tags: [],
        isRemoving: false,
        isUploading: false,
      },
    ]);
    setError(null);
    onClose();
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      className='modal-xl'
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {title}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          {error && (
            <div className='alert alert-danger' role='alert'>
              {error}
            </div>
          )}

          {/* Media Items List */}
          <div className='media-items-container'>
            {mediaItems.map((item) => (
              <div 
                key={item.id}
                className={`media-item-card ${item.isRemoving ? 'removing' : ''}`}
              >
                <div className='media-item-header'>
                  <h6 className='media-item-title'>Media Item #{item.id + 1}</h6>
                  {mediaItems.length > 1 && (
                    <Button
                      variant="link"
                      className='media-item-remove-link'
                      onClick={() => handleRemoveItem(item.id)}
                      disabled={isLoading}
                    >
                      ✕ Remove
                    </Button>
                  )}
                </div>

                <Row className='mb-3'>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Name</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Name will be set after upload"
                        value={item.name}
                        disabled
                        readOnly
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>
                        File Type <span className='text-danger'>*</span>
                      </Form.Label>
                      <Form.Select
                        value={item.fileType}
                        className='form-control'
                        onChange={(e) => handleFileTypeChange(item.id, e)}
                        required
                      >
                        <option value="image">Wallpaper</option>
                        <option value="video">Video</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row className='mb-3'>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label>Upload File</Form.Label>
                      {!item.imageUrl ? (
                        <>
                          <Form.Control
                            type="file"
                            accept={getFileAccept(item.fileType)}
                            onChange={(e) => handleFileChange(item.id, e)}
                            disabled={item.isUploading}
                          />
                          {item.previewUrl && (
                            <div className='file-preview-container mt-3'>
                              {item.fileType === 'video' ? (
                                <video
                                  src={item.previewUrl}
                                  muted
                                  preload="metadata"
                                  playsInline
                                  style={{ maxHeight: 180, width: '100%', borderRadius: 8, objectFit: 'cover', backgroundColor: '#000' }}
                                />
                              ) : (
                                <img
                                  src={item.previewUrl}
                                  alt="preview"
                                  style={{ maxHeight: 180, width: '100%', objectFit: 'cover', borderRadius: 8 }}
                                />
                              )}
                            </div>
                          )}
                          {item.isUploading && (
                            <div className='upload-progress-container'>
                              <div className='spinner-border spinner-border-sm' role='status'>
                                <span className='visually-hidden'>Uploading...</span>
                              </div>
                              <span className='ms-2'>Uploading image to backend...</span>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className='file-preview-container'>
                          {item.previewUrl && item.fileType === 'video' ? (
                            <video
                              src={item.previewUrl}
                              className='file-preview-image'
                              muted
                              controls={false}
                              preload="metadata"
                              playsInline
                              style={{ maxHeight: 240, width: '100%', borderRadius: 8, objectFit: 'cover', backgroundColor: '#000' }}
                            />
                          ) : (
                            <img 
                              src={item.previewUrl}
                              alt="preview"
                              className='file-preview-image'
                              style={{ maxHeight: 240, width: '100%', objectFit: 'cover', borderRadius: 8 }}
                            />
                          )}
                          <div className='file-preview-info'>
                            <div className='file-name'>{item.fileName}</div>
                            <div className='file-size'>
                              Uploaded successfully
                            </div>
                            <Button 
                              variant="danger" 
                              size="sm"
                              onClick={() => handleRemoveImage(item.id)}
                              className='mt-2'
                            >
                              Remove Image
                            </Button>
                          </div>
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                <Row className='mb-4'>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label>
                        Tags <span className='text-danger'></span>
                      </Form.Label>
                      <TagsInput
                        value={item.tags}
                        onChange={(tags) => handleTagsChange(item.id, tags)}
                        placeholder="Add tags and press Enter"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {mediaItems.length > 1 && (
                  <div className='media-item-divider'></div>
                )}
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <Row className='mt-4 mb-3'>
            <Col md={12}>
              <Button 
                variant="outline-primary"
                onClick={handleAddItem}
                disabled={isLoading}
                className='w-100'
              >
                + Add More Media
              </Button>
            </Col>
          </Row>

          <Row className='mt-4'>
            <Col md={12} className='d-flex gap-2 justify-content-end'>
              <Button 
                variant="secondary" 
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button 
                variant="primary" 
                type="submit" 
                disabled={isLoading || mediaItems.filter(item => item.imageUrl && item.fileType).length === 0}
                // disabled={isLoading || mediaItems.filter(item => item.imageUrl && item.fileType && item.tags?.length > 0).length === 0}
              >
                {isLoading ? 'Submitting...' : 'Submit'}
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal.Body>
    </Modal>
  )
}

export default ContentModal
